/* eslint-disable @typescript-eslint/no-floating-promises */
import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { DesignMaterialDialog } from "~/pages/marine-survey-and-design-page/dialogs/DesignMaterialDialog";
import { GenericPopup } from "../../popups/GenericPopup";

type Props = ClickProps | HoverProps;

export type CesiumGeoJsonFeatureProperties = {
  Color?: number;
  EntityHandle?: string;
  Layer?: string;
  SubClasses?: string;
  mapId: string;
  type: "cad";
  designMaterialId: number;
};

const IgnoredKeys = [
  "type",
  "mapId",
  "designMaterialId",
  "popupOriginalPos",
  "Color",
];

type ClickProps = {
  clickData: CesiumGeoJsonFeatureProperties;
  hoverData?: CesiumGeoJsonFeatureProperties;
};

type HoverProps = {
  hoverData: CesiumGeoJsonFeatureProperties;
  clickData?: CesiumGeoJsonFeatureProperties;
};

export const CesiumCadPopup = ({
  hoverData,
  clickData,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("cesium-cad");
  const [designMaterialToViewId, setDesignMaterialToViewId] =
    React.useState<number>();
  const { show } = usePopup();

  React.useEffect(() => {
    if (designMaterialToViewId === undefined) return;
    show(
      ({ close }) => (
        <DesignMaterialDialog
          onClose={() => {
            close();
            setDesignMaterialToViewId(undefined);
          }}
          action="view"
          materialId={designMaterialToViewId}
        />
      ),
      { noDialog: true },
    );
  }, [designMaterialToViewId, show]);

  const data = clickData ?? hoverData;

  const propertyKeys = React.useMemo(
    () =>
      Object.keys(data ?? {})?.filter(
        (k) => !IgnoredKeys.includes(k),
      ) as (keyof CesiumGeoJsonFeatureProperties)[],
    [data],
  );

  if (!data) return null;

  return (
    <GenericPopup
      clicked={!!clickData}
      avatarText="CAD"
      title=""
      subheader=""
      content={
        <>
          <Button
            sx={{ ml: "auto" }}
            variant="contained"
            color="secondary"
            startIcon={<InfoIcon />}
            onClick={() => setDesignMaterialToViewId(data.designMaterialId)}
          >
            {t("general:information")}
          </Button>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("property")}</TableCell>
                <TableCell align="right">{t("value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {propertyKeys.map((propKey) => (
                <TableRow key={propKey}>
                  <TableCell component="th" scope="row">
                    {propKey}
                  </TableCell>
                  <TableCell align="right">{data[propKey]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      }
    />
  );
};
